import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Tooltip,
    Collapse
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { format, parseISO } from 'date-fns';
import { useApi } from '../hooks/useApi';
import DetailsTable from './DetailsTable';
import { useSearch } from '../contexts/SearchContext';

interface DetailItem {
    FromTo: string;
    Hrs: number;
    Lateral: number;
    Phase: string;
    Cat: string;
    MajorOP: string;
    Action: string;
    Object: string;
    Responsible: string;
    Summary: string;
    Content: string;
    Rig: string;
    Date: string;
    Well: string;
    FileUrl: string;
    [key: string]: any;
}

interface Report {
    Id: string;
    Rig: string;
    Date: string;
    Well: string;
    FileUrl: string;
    Details: DetailItem[];
    Last24: string;
    Next24: string;
    Foreman: string;
    Engineer: string;
    Manager: string;
    Location: string;
    Content: string;
    Contact: string;
    Hole: string;
    [key: string]: any;
}

const RigReports: React.FC = () => {
    const { rigName } = useParams<{ rigName: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { callApi } = useApi();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [reports, setReports] = useState<Report[]>([]);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const { selectedDate, setSelectedDate, selectedLocation } = useSearch();

    // Define the column configuration
    const defaultColumns = [
        { name: 'Date', width: '110px' },
        { name: 'Well', width: '150px' },
        { name: 'Last24', flex: 1 },
        { name: 'Next24', flex: 1 },
        { name: 'Manager', width: '220px' },
        { name: 'FileUrl', width: '160px' }
    ];

    const omanColumns = [
        { name: 'Date', width: '110px' },
        { name: 'Well', width: '150px' },
        { name: 'Hole', width: '100px' },
        { name: 'Contact', width: '150px' },
        { name: 'Summary', flex: 1 },
        { name: 'FileUrl', width: '160px' }
    ];

    // Get all unique column names from the reports, excluding Details, LostTimeDetails, and Id
    const availableColumns = Array.from(
        new Set(reports.flatMap(report => Object.keys(report)))
    )
    .filter(col => !['Details', 'LostTimeDetails', 'Id', 'CreatedAt', 'SectionIndex', 'CuId', 'Content', 'Location'].includes(col));

    // Filter columns based on availability and location
    const visibleColumns = (selectedLocation === 'Oman' ? omanColumns : defaultColumns)
        .filter(col => availableColumns.includes(col.name));

    useEffect(() => {
        const fetchRigReports = async () => {
            try {
                setLoading(true);
                setError(null);
                const formattedDate = format(selectedDate, 'yyyy-MM-dd');
                const loc = selectedLocation ? `&location=${encodeURIComponent(selectedLocation)}` : '';
                const response = await callApi(`/api/Reports/table?searchTerm=${encodeURIComponent(rigName || '')}&reportDate=${formattedDate}${loc}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch rig reports');
                }
                const data = await response.json();
                // Sort reports by date in descending order
                const sortedReports = data.sort((a: any, b: any) =>
                    new Date(b.Date).getTime() - new Date(a.Date).getTime()
                );
                setReports(sortedReports);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch rig reports');
            } finally {
                setLoading(false);
            }
        };

        // Only fetch if we don't have data or if the rig name has changed
        if (rigName && (!reports.length || reports[0]?.Rig !== rigName)) {
            fetchRigReports();
        }
    }, [rigName, callApi, reports.length, selectedDate]);

    const handleRerun = async (reportId: string) => {
        try {
            const response = await callApi(`/api/Reports/${reportId}/rerun`, {
                method: 'POST'
            });
            if (response.ok) {
                // Refresh the reports
                const formattedDate = format(selectedDate, 'yyyy-MM-dd');
                const loc = selectedLocation ? `&location=${encodeURIComponent(selectedLocation)}` : '';
                const updatedResponse = await callApi(`/api/Reports/table?searchTerm=${encodeURIComponent(rigName || '')}&reportDate=${formattedDate}${loc}`);
                if (updatedResponse.ok) {
                    const data = await updatedResponse.json();
                    const sortedReports = data.sort((a: any, b: any) =>
                        new Date(b.Date).getTime() - new Date(a.Date).getTime()
                    );
                    setReports(sortedReports);
                }
            }
        } catch (err) {
            console.error('Rerun report error:', err);
        }
    };

    const handleRowExpand = (reportId: string) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(reportId)) {
                newSet.delete(reportId);
            } else {
                newSet.add(reportId);
            }
            return newSet;
        });
    };

    const handleBack = () => {
        // Navigate back to the previous page, preserving the search state
        navigate(-1);
    };

    if (loading && !reports.length) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ color: 'white' }}
                >
                    Back to Reports
                </Button>
                <Typography variant="h4">
                    Reports for {rigName}
                </Typography>
            </Box>

            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                {visibleColumns.map((column) => (
                                    <TableCell
                                        key={column.name}
                                        sx={{
                                            fontSize: '0.875rem',
                                            fontWeight: 'bold',
                                            whiteSpace: 'normal',
                                            wordWrap: 'break-word',
                                            width: column.width
                                        }}
                                    >
                                        {column.name === 'FileUrl' ? 'Actions' :
                                         column.name === 'Date' ? 'ReportDate' : column.name}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reports.map((report, index) => (
                                <React.Fragment key={index}>
                                    <TableRow hover>
                                        <TableCell padding="checkbox">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRowExpand(report.Id)}
                                                sx={{
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                {expandedRows.has(report.Id) ?
                                                    <ExpandLessIcon /> :
                                                    <ExpandMoreIcon />
                                                }
                                            </IconButton>
                                        </TableCell>
                                        {visibleColumns.map((column) => (
                                            <TableCell
                                                key={`${index}-${column.name}`}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                    width: column.width
                                                }}
                                            >
                                                {column.name === 'FileUrl' ? (
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        {report[column.name] && (
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                startIcon={<LaunchIcon />}
                                                                href={report[column.name]}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{
                                                                    fontSize: '0.75rem',
                                                                    textTransform: 'none',
                                                                    backgroundColor: 'transparent',
                                                                    color: 'white',
                                                                    '& .MuiSvgIcon-root': {
                                                                        color: 'white'
                                                                    },
                                                                    '&:hover': {
                                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                                    }
                                                                }}
                                                            >
                                                                Open Report
                                                            </Button>
                                                        )}
                                                        <Tooltip title="Rerun Report">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRerun(report.Id)}
                                                                sx={{
                                                                    color: 'white',
                                                                    '&:hover': {
                                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                                    }
                                                                }}
                                                            >
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ) : column.name === 'Date' ? (
                                                    format(parseISO(report[column.name]), 'yyyy-MM-dd')
                                                ) : column.name === 'Summary' ? (
                                                    report.Summary || report.Summary || ''
                                                ) : column.name === 'Contact' ? (
                                                    report.Contact || report.Manager || ''
                                                ) : column.name === 'Hole' ? (
                                                    report.Hole || ''
                                                ) : (
                                                    report[column.name] || ''
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                                            <Collapse in={expandedRows.has(report.Id)} timeout="auto" unmountOnExit>
                                                <Box sx={{ margin: 1 }}>
                                                    <Typography variant="h6" gutterBottom component="div">
                                                        Details for {report.Rig} - {format(parseISO(report.Date), 'yyyy-MM-dd')}
                                                    </Typography>
                                                    {selectedLocation !== 'Oman' && (
                                                        <DetailsTable
                                                            hideSearch={true}
                                                            initialData={report.Details?.map((detail: DetailItem) => ({
                                                                FromTo: detail.fromTo || '',
                                                                Hrs: detail.hrs || 0,
                                                                Lateral: detail.lateral || 0,
                                                                Phase: detail.phase || '',
                                                                Cat: detail.cat || '',
                                                                MajorOP: detail.majorOP || '',
                                                                Action: detail.action || '',
                                                                Object: detail.object || '',
                                                                Responsible: detail.responsible || '',
                                                                Summary: detail.summary || '',
                                                                Content: detail.content || '',
                                                                Rig: report.Rig || '',
                                                                Date: report.Date || '',
                                                                Well: report.Well || '',
                                                                FileUrl: report.FileUrl || ''
                                                            })) || []}
                                                        />
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default RigReports;
